import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { apiService } from "../api/apiService";
import { ROUTE_MAP } from "../constants/routes";

/**
 * Lekki sygnał aktywności dla modułu „Analityka czasu pracy" (panel admina).
 * - ping bieżącej strony przy każdej zmianie trasy (gdzie pracuje user),
 * - heartbeat co 60 s, ale TYLKO gdy karta jest widoczna (realny czas pracy w aplikacji).
 * Zapisuje wyłącznie WŁASNĄ aktywność (backend bierze userId z tokenu). Brak jakiegokolwiek UI.
 * Błędy są ciche — telemetria nie może wpływać na działanie aplikacji.
 */
const HEARTBEAT_MS = 60_000;

/* [DWS-01, przegląd bezpieczeństwa 2026-09-26] STRONA SYGNALISTY NIE ZOSTAWIA PULSU.
   BYŁO ŹLE: każda zmiana trasy i puls co 60 s wysyłały `/sygnalista` z tokenem pracownika, a analityka
   czasu pracy pokazuje te wpisy per osoba z dokładną godziną. SKUTEK DLA UŻYTKOWNIKA: osoba z dostępem
   do analityki widziała, kto i kiedy wszedł w kanał zgłoszeń — to zdradzało anonimowego zgłaszającego.
   NAPRAWA: na stronie Sygnalisty nic nie wysyłamy (ani przy wejściu, ani w pulsie). Serwer i tak odrzuca
   taką ścieżkę (WorkTimeController.SciezkiSygnalisty) — to obrona dla starej karty ze starym bundlem.
   Strona obsługi spraw (`hrSygnalista`) to praca osób prowadzących sprawy i zostaje. */
const SCIEZKI_SYGNALISTY = [ROUTE_MAP.sygnalista.pl, ROUTE_MAP.sygnalista.en].map((s) => s.toLowerCase());

/* [PII-02-a, poprawka przeglądu 2026-09-26] STRONY ANKIET TEŻ BEZ PULSU (lista i wypełnianie; panel zarządzania jest pod /admin).
   BYŁO ŹLE: PII-02 zamknęło kanał API, ale puls zapisywał `/ankiety/{kod}` z UserId i minutą, a po wysłaniu odpowiedzi
   strona przechodzi na `/ankiety` — ping w sekundzie wysłania. SKUTEK: analityka pokazywała, kto danego dnia wypełniał
   ankietę; przy jednej odpowiedzi tego dnia data w wynikach ankiety ANONIMOWEJ wskazywała autora. Serwer odrzuca te
   ścieżki tak samo (WorkTimeController.SciezkiAnkiet) — obrona dla starej karty. */
const SCIEZKI_ANKIET = [ROUTE_MAP.surveys.pl, ROUTE_MAP.surveys.en].map((s) => s.toLowerCase());

const pasujeDo = (sciezka, trasy) => {
    if (!sciezka || typeof sciezka !== "string") return false;
    const s = sciezka.split(/[?#]/)[0].trim().replace(/\/+$/, "").toLowerCase();
    return trasy.some((r) => s === r || s.startsWith(r + "/"));
};

export const czyStronaSygnalisty = (sciezka) => pasujeDo(sciezka, SCIEZKI_SYGNALISTY);

export const czyStronaAnkiety = (sciezka) => pasujeDo(sciezka, SCIEZKI_ANKIET);

/** Strony, których puls nie trafia do analityki czasu pracy (DWS-01 + PII-02-a). */
export const czyStronaBezSladu = (sciezka) => czyStronaSygnalisty(sciezka) || czyStronaAnkiety(sciezka);

const ActivityTracker = () => {
    const location = useLocation();
    const lastPathRef = useRef(null);
    const naStronieBezSladuRef = useRef(false);

    // Ping przy każdej zmianie trasy.
    useEffect(() => {
        const page = location.pathname;
        const bezSladu = czyStronaBezSladu(page);
        naStronieBezSladuRef.current = bezSladu;
        if (bezSladu) {
            lastPathRef.current = null;
            return;
        }
        lastPathRef.current = page;
        apiService.trackActivity(page).catch(() => {});
    }, [location.pathname]);

    // Heartbeat co 60 s — tylko gdy karta widoczna.
    useEffect(() => {
        const tick = () => {
            if (document.visibilityState !== "visible" || naStronieBezSladuRef.current) return;
            const page = lastPathRef.current || window.location.pathname;
            if (czyStronaBezSladu(page)) return;
            apiService.trackActivity(page).catch(() => {});
        };
        const id = setInterval(tick, HEARTBEAT_MS);
        return () => clearInterval(id);
    }, []);

    return null;
};

export default ActivityTracker;
